import { Box, Chip, Divider, IconButton, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { useSession } from '@/auth';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { TimeLogLogo } from '@/components/TimeLogLogo';
import { ForcedFlagsChip } from '@/featureFlags';

/**
 * The top bar, following
 * Web/src/components/global/TopNavigation/TopNavigation.module.scss — except its
 * height: 48px since 2026-10-01 (the pilot's ask: less air around the logo),
 * where Web's is 66px.
 *
 * It has no background and no bottom border — it sits directly on the grey app
 * body, and the white page card below it is what creates the visual separation.
 *
 * It also carries **no user identity**. The name, company and Logout live at the
 * bottom of the sidebar, matching TimeLog: `TopNavigation.tsx` in Web has no
 * username, avatar or profile element of any kind.
 */
export type TopNavigationProps = {
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
};

export const TopNavigation = ({ sidebarCollapsed, onToggleSidebar }: TopNavigationProps) => {
  const { t } = useTranslation();
  const session = useSession();
  const toggleLabel = sidebarCollapsed
    ? t('topNavigation.ShowNavigation')
    : t('topNavigation.HideNavigation');

  return (
    <Stack
      component="header"
      direction="row"
      data-automation-id="top-navigation"
      sx={{
        height: 'var(--top-navigation-height)',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'space-between',
        columnGap: 1.5,
        // Asymmetric, as in the design system: 12px right, 1px left. The block
        // padding went with the height (Web: 18px).
        py: 1,
        pr: 1.5,
        pl: '1px',
        flex: 'none',
      }}
    >
      <Stack direction="row" gap={{ xs: 1, sm: 3, lg: 5 }} sx={{ alignItems: 'center' }}>
        <Box
          component={RouterLink}
          to="/"
          data-automation-id="app-brand"
          sx={{
            height: 'var(--top-navigation-height)',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pl: { xs: 0, md: '30px' },
            color: 'var(--fg-1)',
            textDecoration: 'none',
            '&:focus-visible': { outline: 'var(--focus-ring)', outlineOffset: '2px' },
          }}
        >
          <TimeLogLogo />
        </Box>
        <IconButton
          size="small"
          color="secondary"
          onClick={onToggleSidebar}
          aria-label={toggleLabel}
          aria-expanded={!sidebarCollapsed}
          title={toggleLabel}
          data-automation-id="sidebar-toggle"
          sx={{ display: { xs: 'none', md: 'inline-flex' } }}
        >
          <MaterialSymbol
            name={sidebarCollapsed ? 'dock_to_right' : 'dock_to_left'}
            size={20}
            color="var(--fg-1)"
          />
        </IconButton>
      </Stack>

      <Stack
        direction="row"
        gap={{ xs: 0.5, sm: 1 }}
        sx={{ alignItems: 'center' }}
        divider={
          <Divider
            orientation="vertical"
            variant="middle"
            sx={{ height: 18, alignSelf: 'center' }}
          />
        }
      >
        <Stack direction="row" gap={{ xs: 0.5, sm: 1 }} sx={{ alignItems: 'center' }}>
          {session.mode === 'test-login' ? (
            // Makes it unmistakable that this is not real authentication.
            <Chip
              label={t('topNavigation.TestLoginBadge')}
              color="warning"
              size="small"
              data-automation-id="test-login-badge"
            />
          ) : null}
          <ForcedFlagsChip />
        </Stack>
        <Stack direction="row" gap={{ xs: 0.5, sm: 1 }} sx={{ alignItems: 'center' }}>
          <IconButton
            size="small"
            color="secondary"
            component="a"
            href="https://help.timelog.com"
            target="_blank"
            rel="noreferrer"
            aria-label={t('topNavigation.Help')}
            title={t('topNavigation.Help')}
            data-automation-id="help-button"
          >
            <MaterialSymbol name="help" size={20} color="var(--fg-1)" />
          </IconButton>
        </Stack>
      </Stack>
    </Stack>
  );
};
